import { Button } from '@mui/material';
import { usePaperBackgroundColor } from '@shared/theme/hooks';
import { TextField } from '@shared/ui/Inputs';
import { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@/component/Dialog';
import { DataProvider, Table } from '@/component/Table';
import { useDataProvider } from '@/component/Table/DataProviderContext';
import { getInputProps } from '@/component/Table/filter/util';
import StyledTable from '@/component/Table/flat/components/StyledTable';
import { Settings } from '@/component/Table/Settings';
import type {
  ColumnFilterProps,
  ExtendedColumnDef,
} from '@/component/Table/types';
import Translate from '@/component/Translate';
import { useTexts } from '../lang';
import { useAccountSelector } from './AccountProvider';
import { useSelectableAccountsQuery } from './queries';

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
}

function AccountNameFilter({
  column,
  layout,
  onSelectAccount,
}: ColumnFilterProps & {
  onSelectAccount: (account: BusinessEntityBase) => void;
}) {
  const texts = useTexts();
  const { table, isLoading } = useDataProvider<BusinessEntityBase>();

  return (
    <TextField
      autoFocus
      name={column.id}
      value={column.getFilterValue() || ''}
      onChange={(event) =>
        column.setFilterValue(event.target.value || undefined)
      }
      onKeyDown={(event) => {
        if (
          event.key !== 'Enter' ||
          event.nativeEvent.isComposing ||
          isLoading
        ) {
          return;
        }

        const filteredRows = table?.getFilteredRowModel().rows;
        if (filteredRows?.length === 1) {
          event.preventDefault();
          onSelectAccount(filteredRows[0].original);
        }
      }}
      slotProps={{ htmlInput: { 'aria-label': column.columnDef.meta?.name } }}
      {...getInputProps(column, layout, texts)}
    />
  );
}

const AccountDialog = ({ open, onClose }: AccountDialogProps) => {
  const { app, component } = useTexts();
  const { account, changeAccount } = useAccountSelector();
  const { data = [], isLoading } = useSelectableAccountsQuery(open);
  const backgroundColor = usePaperBackgroundColor(24);
  const handleSelectAccount = useCallback(
    (selectedAccount: BusinessEntityBase) => {
      changeAccount(selectedAccount);
      onClose();
    },
    [changeAccount, onClose]
  );
  const columnDefs = useMemo<ExtendedColumnDef<BusinessEntityBase>[]>(
    () => [
      {
        accessorKey: 'displayName',
        headerText: (texts) => texts.admin.company.model.name,
        Filter: (props) => (
          <AccountNameFilter {...props} onSelectAccount={handleSelectAccount} />
        ),
      },
    ],
    [handleSelectAccount]
  );
  const sortedAccounts = useMemo(
    () =>
      [...data].sort((first, second) => {
        if (first.id === account?.id) return -1;
        if (second.id === account?.id) return 1;
        return first.displayName.localeCompare(second.displayName);
      }),
    [data, account?.id]
  );

  const handleActAsYourself = () => {
    changeAccount(undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle title={app.account.dialog.header} />
      <DialogContent sx={{ height: 700 }}>
        <Settings
          showFilterAction={false}
          showActiveFilters={false}
          showColumnFilterOnLoad={true}
        >
          {open && (
            <DataProvider
              columnDefs={columnDefs}
              initialData={sortedAccounts}
              isLoading={isLoading}
              onRowClick={(row) => handleSelectAccount(row.original)}
              isRowHighlighted={(row) => row.original.id === account?.id}
              enablePagination
              enableFilters
            >
              <StyledTable backgroundColor={backgroundColor}>
                <Table />
              </StyledTable>
            </DataProvider>
          )}
        </Settings>
      </DialogContent>
      <DialogActions
        left={
          <Button
            type="button"
            variant="outlined"
            disabled={!account}
            onClick={handleActAsYourself}
          >
            <Translate text={app.account.dialog.viewAsMyself} />
          </Button>
        }
        right={
          <Button type="button" variant="outlined" onClick={onClose}>
            <Translate text={component.button.common.close} />
          </Button>
        }
      />
    </Dialog>
  );
};

export default AccountDialog;
